import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { User, Mail, Phone, MapPin, Heart, Calendar, Briefcase, BookOpen, Save, CheckCircle, AlertCircle } from 'lucide-react';

const FormInfoWidget = ({ userEmail }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [studentRollNumber, setStudentRollNumber] = useState('');

  const [formData, setFormData] = useState({
    rollNumber: '',
    surname: '',
    firstName: '',
    fatherName: '',
    dob: '',
    gender: '',
    currentAddress: '',
    permanentAddress: '',
    aadharCard: '',
    bloodGroup: '',
    abcId: '',
    hobbies: '',
    mobile: '',
    whatsapp: '',
    email: '',
    fatherMobile: '',
    fatherOccupation: '',
    motherName: '',
    motherMobile: '',
    motherOccupation: ''
  });

  useEffect(() => {
    loadStudentData();
  }, [userEmail]);

  const loadStudentData = async () => {
    setLoading(true);
    try {
      // Find student by email
      const studentsSnapshot = await getDoc(doc(db, 'students', studentRollNumber || 'FB101'));
      
      if (studentsSnapshot.exists()) {
        const studentData = studentsSnapshot.data();
        setStudentRollNumber(studentData.rollNumber);
        setFormData({
          rollNumber: studentData.rollNumber || '',
          surname: studentData.surname || '',
          firstName: studentData.firstName || '',
          fatherName: studentData.fatherName || '',
          dob: studentData.dob || '',
          gender: studentData.gender || '',
          currentAddress: studentData.currentAddress || '',
          permanentAddress: studentData.permanentAddress || '',
          aadharCard: studentData.aadharCard || '',
          bloodGroup: studentData.bloodGroup || '',
          abcId: studentData.abcId || '',
          hobbies: studentData.hobbies || '',
          mobile: studentData.mobile || '',
          whatsapp: studentData.whatsapp || '',
          email: studentData.email || '',
          fatherMobile: studentData.fatherMobile || '',
          fatherOccupation: studentData.fatherOccupation || '',
          motherName: studentData.motherName || '',
          motherMobile: studentData.motherMobile || '',
          motherOccupation: studentData.motherOccupation || ''
        });
        setIsEditing(true);
      }
    } catch (err) {
      console.error('Error loading student data:', err);
      setError('Failed to load student data');
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setSuccess(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.rollNumber || !formData.firstName || !formData.surname) {
      setError('Please fill in all required fields (Roll Number, First Name, Surname)');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const studentRef = doc(db, 'students', formData.rollNumber);
      
      const dataToSave = {
        ...formData,
        updatedAt: new Date().toISOString()
      };

      if (isEditing) {
        await updateDoc(studentRef, dataToSave);
      } else {
        await setDoc(studentRef, dataToSave, { merge: true });
      }

      setSuccess(true);
      setIsEditing(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving data:', err);
      setError('Failed to save data. Please try again.');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md max-w-4xl mx-auto">
      {/* Header - Google Forms Style */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-lg p-8 text-white">
        <h1 className="text-3xl font-bold mb-2">Student Information Form</h1>
        <p className="text-blue-100">YBIT Mentoring System - Complete your profile information</p>
        {isEditing && (
          <div className="mt-4 bg-blue-500 bg-opacity-50 rounded px-4 py-2 text-sm">
            ✏️ Editing existing information
          </div>
        )}
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mx-8 mt-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-800 font-medium">Information saved successfully!</span>
        </div>
      )}

      {error && (
        <div className="mx-8 mt-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-8 space-y-8">
        {/* Basic Information Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2 pb-2 border-b-2 border-gray-200">
            <User className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-800">Basic Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Roll Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="rollNumber"
                value={formData.rollNumber}
                onChange={handleChange}
                disabled
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                placeholder="e.g., FB101"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter first name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Surname <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="surname"
                value={formData.surname}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter surname"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Date of Birth
              </label>
              <input
                type="text"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="DD/MM/YYYY"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Heart className="w-4 h-4 inline mr-1" />
                Blood Group
              </label>
              <select
                name="bloodGroup"
                value={formData.bloodGroup}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Blood Group</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2 pb-2 border-b-2 border-gray-200">
            <Phone className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-800">Contact Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email ID
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="student@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Mobile Number
              </label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="9876543210"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Number</label>
              <input
                type="tel"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="9876543210"
              />
            </div>
          </div>
        </div>

        {/* Address Information Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2 pb-2 border-b-2 border-gray-200">
            <MapPin className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-800">Address Information</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Address</label>
            <textarea
              name="currentAddress"
              value={formData.currentAddress}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your current address"
            ></textarea>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permanent Address</label>
            <textarea
              name="permanentAddress"
              value={formData.permanentAddress}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your permanent address"
            ></textarea>
          </div>
        </div>

        {/* Documents & ID Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2 pb-2 border-b-2 border-gray-200">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-800">Documents & Academic</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Aadhar Card Number</label>
              <input
                type="text"
                name="aadharCard"
                value={formData.aadharCard}
                onChange={handleChange}
                maxLength="12"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="123456789012"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ABC ID</label>
              <input
                type="text"
                name="abcId"
                value={formData.abcId}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter ABC ID"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Personal Hobbies / Interests</label>
            <textarea
              name="hobbies"
              value={formData.hobbies}
              onChange={handleChange}
              rows="2"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Reading, Sports, Music, etc."
            ></textarea>
          </div>
        </div>

        {/* Parent Information Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2 pb-2 border-b-2 border-gray-200">
            <User className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-800">Parent Information</h2>
          </div>

          {/* Father's Information */}
          <div className="bg-blue-50 rounded-lg p-6 space-y-4">
            <h3 className="font-semibold text-gray-800 mb-4">Father's Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Father's name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Mobile Number
                </label>
                <input
                  type="tel"
                  name="fatherMobile"
                  value={formData.fatherMobile}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="9876543210"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Briefcase className="w-4 h-4 inline mr-1" />
                  Occupation
                </label>
                <input
                  type="text"
                  name="fatherOccupation"
                  value={formData.fatherOccupation}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Business, Service, etc."
                />
              </div>
            </div>
          </div>

          {/* Mother's Information */}
          <div className="bg-pink-50 rounded-lg p-6 space-y-4">
            <h3 className="font-semibold text-gray-800 mb-4">Mother's Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  name="motherName"
                  value={formData.motherName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Mother's name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Mobile Number
                </label>
                <input
                  type="tel"
                  name="motherMobile"
                  value={formData.motherMobile}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="9876543210"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Briefcase className="w-4 h-4 inline mr-1" />
                  Occupation
                </label>
                <input
                  type="text"
                  name="motherOccupation"
                  value={formData.motherOccupation}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Homemaker, Service, etc."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-6 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="w-full md:w-auto px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-colors"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>{isEditing ? 'Update Information' : 'Submit Information'}</span>
              </>
            )}
          </button>
        </div>

        <p className="text-sm text-gray-500 text-center">
          * All information is securely stored in the YBIT Mentoring System database
        </p>
      </form>
    </div>
  );
};

export default FormInfoWidget;